/**
 * Utility to format AI provider error messages into user-friendly text
 */

interface FormattedError {
  /** User-friendly error message */
  message: string;
  /** Whether this is a temporary/retryable error */
  isRetryable: boolean;
  /** Optional suggestion for the user */
  suggestion?: string;
}

/**
 * Detects and formats AI provider error messages into user-friendly text.
 * Returns null if the content is not an error message.
 */
export function formatProviderError(content: string): FormattedError | null {
  // Check if this looks like an error message
  if (!content.startsWith('Error:')) {
    return null;
  }

  const lowerContent = content.toLowerCase();

  // Anthropic overloaded error
  // Pattern: Error: {"type":"error","error":{"type":"overloaded_error",...}}
  if (lowerContent.includes('overloaded_error') || lowerContent.includes('"type":"overloaded"')) {
    return {
      message: 'This AI provider is temporarily busy.',
      isRetryable: true,
      suggestion: 'Please try again in a moment.',
    };
  }

  // Google/Gemini overloaded or unavailable
  // Pattern: Error: got status: 503 ... "model is overloaded"
  if (
    (lowerContent.includes('503') || lowerContent.includes('unavailable')) &&
    (lowerContent.includes('overloaded') || lowerContent.includes('service unavailable'))
  ) {
    return {
      message: 'This AI provider is temporarily busy.',
      isRetryable: true,
      suggestion: 'Please try again in a moment.',
    };
  }

  // Rate limit errors
  if (lowerContent.includes('rate_limit') || lowerContent.includes('rate limit') || lowerContent.includes('429')) {
    return {
      message: 'Rate limit reached for this AI provider.',
      isRetryable: true,
      suggestion: 'Please wait a moment before trying again.',
    };
  }

  // Quota/billing errors
  if (lowerContent.includes('quota') || lowerContent.includes('billing') || lowerContent.includes('insufficient')) {
    return {
      message: 'Service quota exceeded.',
      isRetryable: false,
      suggestion: 'Please contact support if this persists.',
    };
  }

  // Timeout errors
  if (lowerContent.includes('timeout') || lowerContent.includes('timed out')) {
    return {
      message: 'The request timed out.',
      isRetryable: true,
      suggestion: 'Please try again.',
    };
  }

  // Authentication errors
  if (lowerContent.includes('unauthorized') || lowerContent.includes('authentication') || lowerContent.includes('401')) {
    return {
      message: 'Authentication error.',
      isRetryable: false,
      suggestion: 'Please log out and log back in.',
    };
  }

  // Generic server errors (500, 502, 504)
  if (lowerContent.includes('500') || lowerContent.includes('502') || lowerContent.includes('504') || lowerContent.includes('internal server error')) {
    return {
      message: 'The AI provider encountered a server error.',
      isRetryable: true,
      suggestion: 'Please try again in a moment.',
    };
  }

  // If it starts with Error: but we don't recognize the pattern, return a generic message
  return {
    message: 'An error occurred with this AI provider.',
    isRetryable: true,
    suggestion: 'Please try again.',
  };
}

/**
 * Formats error content for display.
 * Returns formatted error message if content is an error, otherwise returns original content.
 */
export function formatMessageContent(content: string): { content: string; isError: boolean } {
  const formattedError = formatProviderError(content);

  if (formattedError) {
    const parts = [formattedError.message];
    if (formattedError.suggestion) {
      parts.push(formattedError.suggestion);
    }
    return {
      content: parts.join(' '),
      isError: true,
    };
  }

  return {
    content,
    isError: false,
  };
}
